import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { FileText, Loader2, Sparkles, RefreshCw, Package, Eye } from "lucide-react";
import { toast } from "sonner";
import PolicyPreviewDialog from "@/components/PolicyPreviewDialog";

function TierCard({ sector, tier, job, lastJob, onAction, busy }) {
  const complete = tier.ready >= tier.total;
  const running = job && job.tier === tier.tier;
  return (
    <div className="rounded-xl border border-border p-4 bg-background" data-testid={`policy-tier-${sector}-${tier.tier}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="font-medium text-sm">{tier.pack_title}</p>
          <p className="text-xs text-muted-foreground">{tier.ready} / {tier.total} policies written</p>
        </div>
        {tier.has_real_files ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`policy-files-ready-${sector}-${tier.tier}`}>Documents live</Badge>
          : <Badge variant="secondary" className="bg-amber-100 text-amber-800" data-testid={`policy-files-placeholder-${sector}-${tier.tier}`}>Placeholder</Badge>}
      </div>
      <Progress value={tier.total ? (tier.ready / tier.total) * 100 : 0} className="h-1.5 mb-3" />
      {running && (
        <p className="text-xs text-primary mb-2 flex items-center gap-1.5" data-testid={`policy-job-progress-${sector}-${tier.tier}`}>
          <Loader2 className="w-3 h-3 animate-spin" />
          {job.status === "building" ? "Building Word & PDF documents…" : `Writing ${job.done + job.failed} / ${job.total}${job.last_title ? ` · ${job.last_title}` : ""}`}
        </p>
      )}
      {!running && lastJob?.status === "paused" && (
        <p className="text-xs text-amber-700 mb-2" data-testid={`policy-job-paused-${sector}-${tier.tier}`}>Paused — Universal Key balance or daily spend limit reached. Top up / wait for the reset, then press Continue writing.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy || !!job} onClick={() => onAction("generate", tier.tier)} data-testid={`policy-generate-${sector}-${tier.tier}`}>
          <Sparkles className="w-3.5 h-3.5 mr-1" /> {complete ? "Regenerate missing" : tier.ready ? "Continue writing" : "Write with AI"}
        </Button>
        {complete && (
          <Button size="sm" variant="outline" disabled={busy || !!job} onClick={() => onAction("build", tier.tier)} data-testid={`policy-build-${sector}-${tier.tier}`}>
            <Package className="w-3.5 h-3.5 mr-1" /> Rebuild documents
          </Button>
        )}
      </div>
      {tier.files.length > 0 && (
        <ul className="mt-3 space-y-1">
          {tier.files.map((f) => (
            <li key={f.id} className="text-xs text-muted-foreground flex items-center gap-1.5"><FileText className="w-3 h-3" /> {f.original_filename} · {(f.size / 1024).toFixed(0)} KB</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PolicyList({ sector, onPreview, onRegenerate, regenerating }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/admin/policy-content/${sector}/policies`).then((r) => setItems(r.data)).catch((e) => logError("policy-list", e)); }, [sector, regenerating]);
  if (!items) return <p className="text-xs text-muted-foreground">Loading…</p>;
  return (
    <ul className="grid sm:grid-cols-2 gap-1 text-xs" data-testid={`policy-list-${sector}`}>
      {items.map((p) => (
        <li key={p.title} className="flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1.5">
          <span className="truncate"><span className="text-muted-foreground mr-1">{p.tier === "starter" ? "S" : "C"}</span>{p.title}</span>
          <span className="flex items-center gap-1 shrink-0">
            {p.status === "ready" ? (
              <>
                <button type="button" className="text-primary hover:underline" onClick={() => onPreview(p.title)} data-testid={`policy-preview-${sector}-${p.title}`}><Eye className="w-3.5 h-3.5" /></button>
                <button type="button" className="text-muted-foreground hover:text-foreground" disabled={regenerating === p.title} onClick={() => onRegenerate(p.title)} data-testid={`policy-regen-${sector}-${p.title}`}>
                  {regenerating === p.title ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </button>
              </>
            ) : <Badge variant="secondary" className={p.status === "failed" ? "bg-red-100 text-red-800" : ""}>{p.status}</Badge>}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function AdminPolicyContent() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [preview, setPreview] = useState(null);
  const [regenerating, setRegenerating] = useState(null);
  const load = useCallback(() => api.get("/admin/policy-content").then((r) => setData(r.data)).catch((e) => logError("policy-content", e)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.active_jobs?.length) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [data, load]);

  const action = async (sector, kind, tier) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/policy-content/${kind}`, { sector, tier });
      toast.success(kind === "generate" ? `Writing ${r.total} policies in the background — this takes a while` : `Documents rebuilt (${r.policies} policies)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };
  const regenerate = async (sector, title) => {
    setRegenerating(title);
    try { await api.post("/admin/policy-content/regenerate-one", { sector, title }); toast.success(`Rewritten: ${title}. Rebuild documents to publish it.`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not regenerate"); }
    finally { setRegenerating(null); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="admin-policy-content">
      <div>
        <p className="font-display font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-accent" /> Policy content — written by AI, delivered as Word + PDF</p>
        <p className="text-xs text-muted-foreground mt-1">Each policy is a full procedure (purpose, legal basis, step-by-step procedure, roles, records, training, audit, inspector focus, appendices). Generation runs in the background — a starter pack takes roughly 15–20 minutes, a complete package an hour or more. Documents replace the placeholders automatically when the pack is complete.</p>
      </div>
      {data.sectors.map((s) => {
        const job = data.active_jobs.find((j) => j.sector === s.sector);
        return (
          <div key={s.sector} className="bg-card border border-border rounded-2xl p-5" data-testid={`policy-sector-${s.sector}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-medium">{s.label}</p>
              <Button size="sm" variant="ghost" onClick={() => setOpen(open === s.sector ? null : s.sector)} data-testid={`policy-toggle-list-${s.sector}`}>{open === s.sector ? "Hide policies" : "Show policies"}</Button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {s.tiers.map((t) => <TierCard key={t.tier} sector={s.sector} tier={t} job={job} lastJob={data.recent_jobs.find((j) => j.sector === s.sector && j.tier === t.tier)} busy={busy} onAction={(kind, tier) => action(s.sector, kind, tier)} />)}
            </div>
            {open === s.sector && <div className="mt-4"><PolicyList sector={s.sector} regenerating={regenerating} onPreview={(title) => setPreview({ sector: s.sector, title })} onRegenerate={(title) => regenerate(s.sector, title)} /></div>}
          </div>
        );
      })}
      {data.recent_jobs.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="policy-recent-jobs">
          Recent: {data.recent_jobs.map((j) => `${j.sector}/${j.tier} ${j.status}${j.failed ? ` (${j.failed} failed)` : ""}`).join(" · ")}
        </p>
      )}
      <PolicyPreviewDialog target={preview} onClose={() => setPreview(null)} onSaved={load} />
    </div>
  );
}
